import { apiClient, clearSessionTokens, getAccessToken, refreshAuthTokensSingleFlight, setAccessToken } from './api'

const REFRESH_TOKEN_KEY = 'refresh_token'

function normalizeToken(token) {
  if (!token) return null
  if (typeof token === 'string') return token
  if (typeof token === 'object' && token.accessToken) return token.accessToken
  if (typeof token === 'object' && token.token) return token.token
  return null
}

function setTokens(token) {
  if (!token) return
  const accessToken = normalizeToken(token)

  if (accessToken) {
    setAccessToken(accessToken)
  }
  // Keep refresh token in HttpOnly cookie and drop legacy localStorage value.
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

function clearTokens() {
  clearSessionTokens()
}

function getRefreshToken() {
  // Legacy fallback during cookie migration; cookie-backed logout is preferred.
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

const REFRESH_CHECK_INTERVAL_MS = 60 * 1000
const ACCESS_TOKEN_REFRESH_BUFFER_MS = 2 * 60 * 1000
let refreshIntervalId = null

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=')
    return JSON.parse(atob(padded))
  } catch (err) {
    return null
  }
}

function getAccessTokenExpiryMs(token) {
  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number') return null
  return payload.exp * 1000
}

async function refreshIfTokenExpiring() {
  const token = getAccessToken()
  if (!token) return false
  const expiryMs = getAccessTokenExpiryMs(token)
  if (!expiryMs) return false

  if (expiryMs - Date.now() <= ACCESS_TOKEN_REFRESH_BUFFER_MS) {
    try {
      await authService.refreshToken()
      return true
    } catch (err) {
      return false
    }
  }

  return false
}

function clearRefreshLoop() {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId)
    refreshIntervalId = null
  }
}

function normalizeUser(user) {
  if (!user || typeof user !== 'object') return null
  return {
    ...user,
    is_admin: user.is_admin ?? user.isAdmin ?? false,
    default_currency: user.default_currency ?? user.defaultCurrency,
    default_language: user.default_language ?? user.defaultLanguage,
    email_verified_at: user.email_verified_at ?? user.emailVerifiedAt,
    created_at: user.created_at ?? user.createdAt,
    updated_at: user.updated_at ?? user.updatedAt,
  }
}

export const authService = {
  async register(name, email, password, passwordConfirmation) {
    const response = await apiClient.post('/auth/register', {
      name,
      email,
      password,
      password_confirmation: passwordConfirmation,
    })
    
    const token = response.data?.data?.token || response.data?.token
    const user = normalizeUser(response.data?.data?.user || response.data?.user)

    if (token) {
      setTokens(token)
      authService.startRefreshLoop()
    }
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    }

    return {
      ...response.data,
      user,
    }
  },

  async login(email, password) {
    const response = await apiClient.post('/auth/login', {
      email,
      password,
    })
    
    const token = response.data?.data?.token || response.data?.token
    const user = normalizeUser(response.data?.data?.user || response.data?.user)

    if (token) {
      setTokens(token)
      authService.startRefreshLoop()
    }
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    }

    return {
      ...response.data,
      user,
    }
  },

  async logout() {
    try {
      const refreshToken = getRefreshToken()
      await apiClient.post('/auth/logout', refreshToken ? { refreshToken } : {})
    } finally {
      clearTokens()
      localStorage.removeItem('user')
      authService.stopRefreshLoop()
    }
  },

  async requestPasswordReset(email) {
    const response = await apiClient.post('/auth/password/forgot', { email })
    return response.data
  },

  async resetPassword(email, token, password, passwordConfirmation) {
    const response = await apiClient.post('/auth/password/reset', {
      email,
      token,
      password,
      password_confirmation: passwordConfirmation,
    })
    return response.data
  },

  async changePassword(currentPassword, password, passwordConfirmation) {
    const response = await apiClient.put('/profile/password', {
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
    })
    return response.data
  },

  async socialLogin(provider, token) {
    const response = await apiClient.post(`/auth/social/${provider}`, { token })

    const tokenPayload = response.data?.data?.token || response.data?.token
    const tok = normalizeToken(tokenPayload)
    const user = normalizeUser(response.data?.data?.user || response.data?.user)

    if (tok) {
      setTokens(tokenPayload)
      authService.startRefreshLoop()
    }
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    }

    return {
      ...response.data,
      user,
    }
  },

  async refreshToken() {
    const accessToken = await refreshAuthTokensSingleFlight()
    if (!accessToken) {
      throw new Error('Refresh token request did not return access token')
    }
    setAccessToken(accessToken)
    authService.startRefreshLoop()
    return { success: true, data: { token: { accessToken } } }
  },

  async hydrateSessionFromRefresh() {
    if (getAccessToken()) {
      authService.startRefreshLoop()
      return true
    }

    try {
      await authService.refreshToken()
      const profile = await apiClient.get('/auth/profile')
      const user = normalizeUser(profile.data?.user || profile.data?.data?.user)
      if (user) {
        localStorage.setItem('user', JSON.stringify(user))
      }
      return true
    } catch (err) {
      clearTokens()
      localStorage.removeItem('user')
      authService.stopRefreshLoop()
      return false
    }
  },

  applySessionFromExternalAuth(token, user) {
    if (!token || !user) return false
    setTokens(token)
    localStorage.setItem('user', JSON.stringify(normalizeUser(user)))
    authService.startRefreshLoop()
    return true
  },

  getCurrentUser() {
    const user = localStorage.getItem('user')
    return user ? normalizeUser(JSON.parse(user)) : null
  },

  async refreshTokenIfNeeded() {
    return refreshIfTokenExpiring()
  },

  startRefreshLoop() {
    clearRefreshLoop()
    if (!getAccessToken()) return
    refreshIfTokenExpiring()
    refreshIntervalId = window.setInterval(refreshIfTokenExpiring, REFRESH_CHECK_INTERVAL_MS)
  },

  stopRefreshLoop() {
    clearRefreshLoop()
  },

  isAuthenticated() {
    return !!getAccessToken()
  },

  getToken() {
    return getAccessToken()
  },
}

export default authService
